import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../lib/api';

const ThemeContext = createContext({});

export function ThemeProvider({ children }) {
  const [prefs, setPrefs] = useState(null); // user preferences
  const [globalTheme, setGlobalTheme] = useState(null); // admin brand colors

  // Load global theme settings (public endpoint — no auth needed)
  useEffect(() => {
    api.get('/settings/theme').then(d => {
      setGlobalTheme(d.theme || {});
      applyGlobalTheme(d.theme || {});
    }).catch(() => {});
  }, []);

  // Load user preferences when logged in
  function loadUserPrefs(userObj) {
    if (!userObj) return;
    const p = {
      dark_mode:            !!userObj.dark_mode,
      timezone:             userObj.timezone             || 'America/New_York',
      date_format:          userObj.date_format          || 'MM/DD/YYYY',
      time_format:          userObj.time_format          || '12hr',
      currency_symbol:      userObj.currency_symbol      || '$',
      theme_primary_color:  userObj.theme_primary_color  || null,
      theme_sidebar_color:  userObj.theme_sidebar_color  || null,
    };
    setPrefs(p);
    applyDarkMode(p.dark_mode);
    // User colors override global — only apply if set
    if (p.theme_primary_color || p.theme_sidebar_color) {
      applyGlobalTheme({
        primary_color: p.theme_primary_color || undefined,
        sidebar_color: p.theme_sidebar_color || undefined,
      });
    }
  }

  function applyGlobalTheme(theme) {
    const root = document.documentElement;
    if (theme.primary_color) {
      root.style.setProperty('--accent', theme.primary_color);
      root.style.setProperty('--accent-hover', darken(theme.primary_color, 15));
      root.style.setProperty('--accent-rgb', hexToRgb(theme.primary_color));
    }
    if (theme.sidebar_color) {
      root.style.setProperty('--sidebar-bg', theme.sidebar_color);
    }
  }

  function applyDarkMode(dark) {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  }

  function updatePref(key, value) {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    if (key === 'dark_mode') applyDarkMode(value);
    api.put('/auth/preferences', { [key]: value }).catch(() => {});
  }

  return (
    <ThemeContext.Provider value={{ prefs, globalTheme, loadUserPrefs, updatePref, applyGlobalTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// ── Helpers ──────────────────────────────────────────────────────
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3), 16);
  const g = parseInt(hex.slice(3,5), 16);
  const b = parseInt(hex.slice(5,7), 16);
  return `${r},${g},${b}`;
}

function darken(hex, amount) {
  let r = parseInt(hex.slice(1,3), 16);
  let g = parseInt(hex.slice(3,5), 16);
  let b = parseInt(hex.slice(5,7), 16);
  r = Math.max(0, r - amount);
  g = Math.max(0, g - amount);
  b = Math.max(0, b - amount);
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}
